import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as s from "../InputPage.styles";
import { Slider } from "@mui/material";
import { styled } from "styled-components";
import SelectModal from "../SelectModal";
import { isMobile } from "../../../component/function/function";
import TaskSection from "./TaskSection";
import { AnalysisForm } from "../machine";
import WeightAlert from "./WeightAlert";

type DropdownKey = "horsepower" | "environment" | "soil" | "slope";

const THUMB = 16;
const hpList = [
  "PTO 90 이상 hp",
  "PTO 71~90 hp",
  "PTO 51~70 hp",
  "PTO 41~50 hp",
  "PTO 31~40 hp",
  "PTO 21~30 hp",
  "PTO 20 hp 이하",
];

const evList = ["매우 좋음", "양호", "보통", "불리", "매우 불리"];

const evDetailMapping: Record<string, string> = {
  "매우 좋음": "평탄, 대규모 농지",
  양호: "보통 평탄한 밭, 2ha 이상 논",
  보통: "0.5~2ha 논, 이앙/콤바인 수확",
  불리: "경사, 수로 혼재한 밭",
  "매우 불리": "장애물, 묘목, 유휴지",
};

const soilList = ["양호", "약간 습함", "습한 토양", "침수/진흙 상태"];

const slopeList = ["평지", "완만한 경사", "중간 경사", "급경사"];

const slopeDetailMapping: Record<string, string> = {
  평지: "0~3°",
  "완만한 경사": "5° 내외",
  "중간 경사": "10° 내외",
  급경사: "15° 이상",
};

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
}

function WeightSection({ formData, setFormData }: Props) {
  const [openImportance, setOpenImportance] = useState(false);
  const [openWeightAlert, setOpenWeightAlert] = useState(false);

  const [percent, setPercent] = useState(0);

  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
  const [activeModalKey, setActiveModalKey] = useState<DropdownKey | null>(
    null
  );

  const { weights, fieldConditions } = formData;

  const getSliderLeft = (value: number) =>
    `calc(${THUMB / 2}px + (100% - ${THUMB}px) * ${value / 100})`;

  const handleChangeWeight = (
    key: keyof AnalysisForm["weights"],
    value: number | number[]
  ) => {
    const v = Array.isArray(value) ? value[0] : value;
    setFormData((prev) => {
      // key 제외한 나머지 weight 합
      const otherSum = Object.entries(prev.weights)
        .filter(([k]) => k !== key)
        .reduce((sum, [, val]) => sum + val, 0);

      // 새로운 합
      const newTotal = otherSum + v;

      // 100 초과 시 → alert + key 값 0으로 유지
      if (newTotal > 100) {
        setOpenWeightAlert(true);
        return {
          ...prev,
          weights: {
            ...prev.weights,
            [key]: v - 10,
          },
        };
      }

      // 정상 입력
      return {
        ...prev,
        weights: {
          ...prev.weights,
          [key]: v,
        },
      };
    });
  };

  //   setFormData((prev) => ({
  //     ...prev,
  //     weights: {
  //       ...prev.weights,
  //       [key]: v,
  //     },
  //   }));
  // };

  const handleChangeFieldCondition = (
    key: keyof AnalysisForm["fieldConditions"],
    value: string | number
  ) => {
    const totalPercent =
      weights.tractorPower +
      weights.workEnvironment +
      weights.soilCondition +
      weights.soilCondition +
      weights.slope +
      weights.skillAndEquipment;
    setFormData((prev) => ({
      ...prev,
      fieldConditions: {
        ...prev.fieldConditions,
        [key]: value as any,
      },
    }));
  };

  const containerRefs = {
    horsepower: useRef<HTMLDivElement | null>(null),
    environment: useRef<HTMLDivElement | null>(null),
    soil: useRef<HTMLDivElement | null>(null),
    slope: useRef<HTMLDivElement | null>(null),
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openDropdown) return;

      const currentRef =
        openDropdown === "horsepower"
          ? containerRefs.horsepower
          : openDropdown === "environment"
            ? containerRefs.environment
            : openDropdown === "soil"
              ? containerRefs.soil
              : containerRefs.slope;

      if (
        currentRef.current &&
        !currentRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const handleSelectClick = (key: DropdownKey) => {
    if (isMobile(window.innerWidth)) {
      setActiveModalKey(key);
      return;
    }
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const valueLeft = useMemo(
    () =>
      `calc(${THUMB / 2}px + (100% - ${THUMB}px) * ${formData.weights.tractorPower / 100})`,
    [formData.weights.tractorPower]
  );

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <SectionHeader>
          <s.SectionTitle>2. 가중치 조정</s.SectionTitle>
          마력, 작업환경, 토양 상태, 숙련도 등 현장 조건을 반영해 효율을
          미세조정할 수 있습니다.
        </SectionHeader>
      </s.SectionTitleContainer>
      <s.SectionInner>
        <s.Accordian onClick={() => setOpenImportance((prev) => !prev)}>
          항목별 중요도
          <s.Arrow
            src="/asset/images/arrow/arrow_left_calendar_black.svg"
            alt=""
            $open={openImportance}
          />
        </s.Accordian>
      </s.SectionInner>
      {openImportance && (
        <>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>트랙터 마력(PTO 출력) 중요도</s.LabelTitle>
            </s.Label>
            <s.SliderContainer>
              <Slider
                value={weights.tractorPower}
                onChange={(_, v) => handleChangeWeight("tractorPower", v)}
                step={10}
                max={100}
                min={0}
                sx={{
                  height: 8,
                  padding: "15px 0",
                  "& .MuiSlider-rail": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#F0F0F0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#166F4D",
                  },
                  "& .MuiSlider-thumb": {
                    width: THUMB,
                    height: THUMB,
                    backgroundColor: "#166F4D",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <s.SliderValue
                style={{ left: getSliderLeft(weights.tractorPower) }}
              >
                {weights.tractorPower}
              </s.SliderValue>
            </s.SliderContainer>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>농지(필드) 상태 중요도</s.LabelTitle>
            </s.Label>
            <s.SliderContainer>
              <Slider
                value={weights.workEnvironment}
                onChange={(_, v) => handleChangeWeight("workEnvironment", v)}
                step={10}
                max={100}
                min={0}
                sx={{
                  height: 8,
                  padding: "15px 0",
                  "& .MuiSlider-rail": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#F0F0F0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#166F4D",
                  },
                  "& .MuiSlider-thumb": {
                    width: THUMB,
                    height: THUMB,
                    backgroundColor: "#166F4D",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <s.SliderValue
                style={{ left: getSliderLeft(weights.workEnvironment) }}
              >
                {weights.workEnvironment}
              </s.SliderValue>
            </s.SliderContainer>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>토양(진흙, 자갈) 상태 중요도</s.LabelTitle>
            </s.Label>
            <s.SliderContainer>
              <Slider
                value={weights.soilCondition}
                onChange={(_, v) => handleChangeWeight("soilCondition", v)}
                step={10}
                max={100}
                min={0}
                sx={{
                  height: 8,
                  padding: "15px 0",
                  "& .MuiSlider-rail": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#F0F0F0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#166F4D",
                  },
                  "& .MuiSlider-thumb": {
                    width: THUMB,
                    height: THUMB,
                    backgroundColor: "#166F4D",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <s.SliderValue
                style={{ left: getSliderLeft(weights.soilCondition) }}
              >
                {weights.soilCondition}
              </s.SliderValue>
            </s.SliderContainer>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>경사(농지 굴곡) 중요도</s.LabelTitle>
            </s.Label>
            <s.SliderContainer>
              <Slider
                value={weights.slope}
                onChange={(_, v) => handleChangeWeight("slope", v)}
                step={10}
                max={100}
                min={0}
                sx={{
                  height: 8,
                  padding: "15px 0",
                  "& .MuiSlider-rail": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#F0F0F0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#166F4D",
                  },
                  "& .MuiSlider-thumb": {
                    width: THUMB,
                    height: THUMB,
                    backgroundColor: "#166F4D",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <s.SliderValue style={{ left: getSliderLeft(weights.slope) }}>
                {weights.slope}
              </s.SliderValue>
            </s.SliderContainer>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>농가 보정치(숙련도 및 장비 상태)</s.LabelTitle>
            </s.Label>
            <s.SliderContainer>
              <Slider
                value={weights.skillAndEquipment}
                onChange={(_, v) => handleChangeWeight("skillAndEquipment", v)}
                step={10}
                max={100}
                min={0}
                sx={{
                  height: 8,
                  padding: "15px 0",
                  "& .MuiSlider-rail": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#F0F0F0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: 8,
                    borderRadius: 100,
                    backgroundColor: "#166F4D",
                  },
                  "& .MuiSlider-thumb": {
                    width: THUMB,
                    height: THUMB,
                    backgroundColor: "#166F4D",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <s.SliderValue
                style={{ left: getSliderLeft(weights.skillAndEquipment) }}
              >
                {weights.skillAndEquipment}
              </s.SliderValue>
            </s.SliderContainer>
          </s.SectionInner>
        </>
      )}
      <s.SectionInner $top={8}>
        <s.Accordian style={{ cursor: "default" }}>실제 현장 조건</s.Accordian>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>작업 환경</s.LabelTitle>
        </s.Label>
        <s.SelectContainer ref={containerRefs.environment}>
          <s.Select
            $active={openDropdown === "environment"}
            onClick={() => {
              handleSelectClick("environment");
            }}
          >
            <div className="select-label">
              {formData.fieldConditions.workEnvironment}
            </div>
            <s.SelectLabelDesc>
              {evDetailMapping[formData.fieldConditions.workEnvironment]}
            </s.SelectLabelDesc>
            <img
              src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
              alt=""
            />
          </s.Select>
          {openDropdown === "environment" && (
            <s.SelectDropdown>
              {evList.map((i) => (
                <s.DropdownItem
                  key={i}
                  className="hover"
                  onClick={() => {
                    handleChangeFieldCondition("workEnvironment", i);
                    setOpenDropdown(null);
                  }}
                >
                  {i}
                  <div className="flex1">{evDetailMapping[i]}</div>
                  {formData.fieldConditions.workEnvironment === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.DropdownItem>
              ))}
            </s.SelectDropdown>
          )}
        </s.SelectContainer>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>토양 상태</s.LabelTitle>
        </s.Label>
        <s.SelectContainer ref={containerRefs.soil}>
          <s.Select
            $active={openDropdown === "soil"}
            onClick={() => {
              handleSelectClick("soil");
            }}
          >
            <div className="select-label flex1">
              {formData.fieldConditions.soilCondition}
            </div>
            <img
              src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
              alt=""
            />
          </s.Select>
          {openDropdown === "soil" && (
            <s.SelectDropdown>
              {soilList.map((i) => (
                <s.DropdownItem
                  key={i}
                  className="hover"
                  onClick={() => {
                    handleChangeFieldCondition("soilCondition", i);
                    setOpenDropdown(null);
                  }}
                >
                  <div className="flex1">{i}</div>
                  {formData.fieldConditions.soilCondition === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.DropdownItem>
              ))}
            </s.SelectDropdown>
          )}
        </s.SelectContainer>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>지형 경사</s.LabelTitle>
        </s.Label>
        <s.SelectContainer ref={containerRefs.slope}>
          <s.Select
            $active={openDropdown === "slope"}
            onClick={() => {
              handleSelectClick("slope");
            }}
          >
            <div className="select-label">{formData.fieldConditions.slope}</div>
            <s.SelectLabelDesc>
              {slopeDetailMapping[formData.fieldConditions.slope]}
            </s.SelectLabelDesc>
            <img
              src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
              alt=""
            />
          </s.Select>
          {openDropdown === "slope" && (
            <s.SelectDropdown>
              {slopeList.map((i) => (
                <s.DropdownItem
                  key={i}
                  className="hover"
                  onClick={() => {
                    handleChangeFieldCondition("slope", i);
                    setOpenDropdown(null);
                  }}
                >
                  {i}
                  <div className="flex1">{slopeDetailMapping[i]}</div>
                  {formData.fieldConditions.slope === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.DropdownItem>
              ))}
            </s.SelectDropdown>
          )}
        </s.SelectContainer>
      </s.SectionInner>

      {activeModalKey === "horsepower" && (
        <SelectModal
          title="트랙터 마력대"
          list={hpList}
          now={formData.fieldConditions.tractorPowerRange}
          handleSelect={(value: string) => {
            handleChangeFieldCondition("tractorPowerRange", value);
          }}
          onClose={() => setActiveModalKey(null)}
        />
      )}
      {activeModalKey === "environment" && (
        <SelectModal
          title="작업환경"
          list={evList}
          now={formData.fieldConditions.workEnvironment}
          handleSelect={(value: string) => {
            handleChangeFieldCondition("workEnvironment", value);
          }}
          detailMapping={evDetailMapping}
          onClose={() => setActiveModalKey(null)}
        />
      )}
      {activeModalKey === "soil" && (
        <SelectModal
          title="토양상태"
          list={soilList}
          now={formData.fieldConditions.soilCondition}
          handleSelect={(value: string) => {
            handleChangeFieldCondition("soilCondition", value);
          }}
          onClose={() => setActiveModalKey(null)}
        />
      )}
      {activeModalKey === "slope" && (
        <SelectModal
          title="지형경사"
          list={slopeList}
          now={formData.fieldConditions.slope}
          handleSelect={(value: string) => {
            handleChangeFieldCondition("slope", value);
          }}
          detailMapping={slopeDetailMapping}
          onClose={() => setActiveModalKey(null)}
        />
      )}

      {openWeightAlert && (
        <WeightAlert onClose={() => setOpenWeightAlert(false)} />
      )}
    </s.Section>
  );
}

export default WeightSection;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;

  color: rgba(23, 30, 38, 0.56);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`;
